import React from 'react';
import { AnimatePresence } from 'motion/react';
import ScorePanel from './ScorePanel';
import ScopaAnimation from './ScopaAnimation';
import LowHandAnimation from './LowHandAnimation';
import RoundSummary from './RoundSummary';
import ProductAnalyticsTracker from './ProductAnalyticsTracker';
import { Card, GameState, PlayerRoundResult } from '../types';
import { GameBoardProvider } from './GameBoardContext';
import { OpponentHand } from './OpponentHand';
import { TableArea } from './TableArea';
import { PlayerArea } from './PlayerArea';
import { RulesPanel } from './RulesPanel';
import { CaptureComboModal } from './CaptureComboModal';
import { CaptureAnimationOverlay } from './CaptureAnimationOverlay';
import { cn } from '../lib/utils';
import { useSettings } from './SettingsContext';
import Footer from './Footer';

interface GameBoardProps {
  gameState: GameState;
  selectedCard: Card | null;
  hoveredCardId: string | null;
  possibleCaptures: Card[][];
  capturingCards: { cards: Card[]; playedCard: Card; playerIndex: number } | null;
  showRules: boolean;
  showCapturesMenu: boolean;
  scopaParticleSeeds: { x: number; y: number; scale: number; rotate: number; duration: number }[];
  errorMessage: string | null;
  roundStats: {
    pScore: number;
    aiScore: number;
    pResults: PlayerRoundResult | null;
    aiResults: PlayerRoundResult | null;
    results: PlayerRoundResult[];
  } | null;
  dealId: number;
  revealedCardIds: Set<string>;
  revealingCardIds: Set<string>;
  viewportWidth: number;
  onCardSelect: (card: Card) => void;
  onCaptureSelect: (capture: Card[]) => void;
  onHoverCard: (id: string | null) => void;
  onToggleRules: () => void;
  onDismissCapturesMenu: () => void;
  onNextRound: () => void;
  onRestart: () => void;
  onExit: () => void;
  onDeclareLowHand: () => void;
  onDeclareThreeAces: () => void;
  playerName?: string;
  isOnline?: boolean;
  isConnected?: boolean;
  onlineOverlay?: React.ReactNode;
  targetScore?: number;
}

export default function GameBoard(props: GameBoardProps) {
  const { gameState, isOnline, isConnected, onRestart, onExit, targetScore, roundStats, onNextRound, dealId } = props;
  const { tableTheme, playSfx, triggerHaptic } = useSettings();

  React.useEffect(() => {
    if (dealId > 0) {
      playSfx('deal');
      triggerHaptic('light');
    }
  }, [dealId, playSfx, triggerHaptic]);

  const settebelloOwner = React.useMemo(() => {
    return gameState.players.find(p => p.captured.some(c => c.suit === 'denari' && c.value === 7))?.id;
  }, [gameState.players]);

  const dueDenariOwner = React.useMemo(() => {
    return gameState.players.find(p => p.captured.some(c => c.suit === 'denari' && c.value === 2))?.id;
  }, [gameState.players]);

  const prevSettebelloOwner = React.useRef<number | undefined>(undefined);
  React.useEffect(() => {
    if (settebelloOwner !== undefined && prevSettebelloOwner.current === undefined) {
      playSfx('settebello');
      triggerHaptic('success');
    }
    prevSettebelloOwner.current = settebelloOwner;
  }, [settebelloOwner, playSfx, triggerHaptic]);

  const prevDueDenariOwner = React.useRef<number | undefined>(undefined);
  React.useEffect(() => {
    if (dueDenariOwner !== undefined && prevDueDenariOwner.current === undefined) {
      playSfx('malus2');
      triggerHaptic('warning');
    }
    prevDueDenariOwner.current = dueDenariOwner;
  }, [dueDenariOwner, playSfx, triggerHaptic]);

  const lowHandDeclaredCount = React.useMemo(() => {
    return gameState.players.filter(p => p.hasDeclaredLowHand).length;
  }, [gameState.players]);

  const threeAcesDeclaredCount = React.useMemo(() => {
    return gameState.players.filter(p => p.hasDeclaredThreeAces).length;
  }, [gameState.players]);

  const prevLowHandCount = React.useRef(0);
  React.useEffect(() => {
    if (lowHandDeclaredCount > prevLowHandCount.current) {
      playSfx('dichiarazione');
      triggerHaptic('success');
    }
    prevLowHandCount.current = lowHandDeclaredCount;
  }, [lowHandDeclaredCount, playSfx, triggerHaptic]);

  const prevThreeAcesCount = React.useRef(0);
  React.useEffect(() => {
    if (threeAcesDeclaredCount > prevThreeAcesCount.current) {
      playSfx('dichiarazione');
      triggerHaptic('success');
    }
    prevThreeAcesCount.current = threeAcesDeclaredCount;
  }, [threeAcesDeclaredCount, playSfx, triggerHaptic]);

  const prevPhase = React.useRef(gameState.phase);
  React.useEffect(() => {
    if (gameState.phase === 'playing' && (prevPhase.current === 'roundEnd' || prevPhase.current === 'gameOver')) {
      playSfx('mescola');
      triggerHaptic('medium');
    }
    prevPhase.current = gameState.phase;
  }, [gameState.phase, playSfx, triggerHaptic]);

  const topPlayerIdx = gameState.numPlayers === 4 ? 2 : 1;
  const leftPlayerIdx = gameState.numPlayers === 4 ? 3 : 2;

  return (
    <GameBoardProvider {...props}>
      <ProductAnalyticsTracker
        gameState={gameState}
        roundResults={roundStats?.results ?? []}
        isOnline={isOnline}
      />
      <div
        className={cn(
          'fixed inset-0 game-bg flex flex-col items-center justify-between p-4 text-white select-none overflow-hidden',
          `game-bg-${tableTheme}`,
        )}
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
        }}
      >
        <ScorePanel
          players={gameState.players}
          currentPlayerIndex={gameState.currentPlayerIndex}
          onRestart={onRestart}
          onExit={onExit}
          isOnline={isOnline}
          isConnected={isConnected}
          targetScore={targetScore}
        />

        <main className="flex-1 w-full max-w-5xl relative px-2" style={{ overflow: 'visible' }}>
          <OpponentHand playerIndex={topPlayerIdx} position="top" />

          {gameState.numPlayers >= 3 && (
            <OpponentHand playerIndex={leftPlayerIdx} position="left" />
          )}

          {gameState.numPlayers === 4 && (
            <OpponentHand playerIndex={1} position="right" />
          )}

          <TableArea />
          <PlayerArea />
          <RulesPanel />
        </main>

        <CaptureAnimationOverlay />
        <CaptureComboModal />

        <AnimatePresence>
          {(gameState.phase === 'roundEnd' || gameState.phase === 'gameOver') && (
            <RoundSummary
              gameState={gameState}
              roundResults={roundStats?.results ?? []}
              onNextRound={onNextRound}
              onRestart={onRestart}
              onExit={onExit}
              isOnline={isOnline}
            />
          )}
        </AnimatePresence>

        <ScopaAnimation
          scopaEvent={gameState.scopaEvent}
          scopaParticleSeeds={props.scopaParticleSeeds}
        />

        <LowHandAnimation lowHandEvent={gameState.lowHandEvent} />

        {/* No advertising is rendered during an active game. */}
        <Footer />

        {props.onlineOverlay}
      </div>
    </GameBoardProvider>
  );
}
